import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BoltIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { faIndustry, faBolt, faPlug, faStore, faBoxOpen, faCarSide } from '@fortawesome/free-solid-svg-icons';
import { fetchProducts } from '../api/fetchProducts';
import Breadcrumbs from '../components/Breadcrumbs';
import AvailabilityBadge from '../components/AvailabilityBadge';
import SpecGrid from '../components/SpecGrid';
import ObrazekAuta from '../components/ObrazekAuta';
import PodpisGrafiki from '../components/PodpisGrafiki';
import { ProductImage } from '../components/ProductImage';
import { formatPrice } from '../utils';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { buildModelUrl } from '../utils/modelUrl';
import { sciezkaLogaMarki, sciezkiZdjeciaAuta } from '../utils/logoAuta';

/**
 * Kreator „dobierz wallbox do auta".
 *
 * To jedyna funkcja tego modułu, której nie da się zastąpić katalogiem z filtrami:
 * odpowiada na pytanie „11 czy 22 kW do MOJEGO auta". Cała logika (moc ładowarki
 * pokładowej, liczba faz, opcjonalne wyposażenie) siedzi po stronie API — tu jest
 * tylko prezentacja, żeby zasady nie rozjechały się między frontem a backendem.
 *
 * Wybrane auto trzymamy w adresie (`?auto=slug`), a nie w stanie komponentu.
 * Dzięki temu powrót z karty produktu wraca do TEGO SAMEGO wyniku, a link do
 * doboru dla konkretnego auta da się wysłać komuś innemu.
 */

// „164 wersje”, „125 wersji”: liczebnik z rzeczownikiem w dobrej formie.
const wersjeAut = (n) => {
  const forma = n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? 'wersje' : 'wersji';
  return `${n} ${n === 1 ? 'wersję' : forma} aut`;
};

const PORADNIKI_OGOLNE = [
  ['/baza-wiedzy/wallbox-11-czy-22-kw', 'Wallbox 11 czy 22 kW? Ile naprawdę przyjmie Twoje auto'],
  ['/baza-wiedzy/ladowanie-auta-z-fotowoltaiki', 'Ładowanie auta z fotowoltaiki: co jest potrzebne'],
  ['/dobor-ladowarki/auta-z-ladowarka-jednofazowa', 'Auta z ładowarką jednofazową: jaki wallbox?'],
  ['/dobor-ladowarki/hybrydy-plug-in', 'Jaki wallbox do hybrydy plug-in?'],
  ['/dobor-ladowarki/auta-z-ladowarka-22-kw', 'Auta z ładowarką 22 kW: kiedy wallbox 22 kW ma sens'],
];

const WERDYKT = {
  optymalny: {
    label: 'Dobrany optymalnie',
    klasa: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
    kropka: 'bg-emerald-500',
  },
  przeplacasz: {
    label: 'Przepłacasz za moc',
    klasa: 'bg-amber-50 text-amber-800 ring-amber-600/20',
    kropka: 'bg-amber-500',
  },
  ponizej_mozliwosci: {
    label: 'Wolniej, niż auto potrafi',
    klasa: 'bg-sky-50 text-sky-700 ring-sky-600/20',
    kropka: 'bg-sky-500',
  },
  niedopasowany: {
    label: 'Nie wykorzysta mocy',
    klasa: 'bg-rose-50 text-rose-700 ring-rose-600/20',
    kropka: 'bg-rose-500',
  },
};

const kw = (v) => (v == null ? 'brak danych' : `${String(v).replace('.', ',')} kW`);

const ofertySlowo = (n) => (n === 1 ? 'oferta' : (n >= 2 && n <= 4 ? 'oferty' : 'ofert'));

/**
 * Kafelek kandydata w układzie reszty platformy: siatka parametrów po lewej,
 * blok ceny i wezwanie do działania po prawej (jak `ModelProduct`). Wcześniej
 * miał własny, luźny układ — a użytkownik, który zna listę ofert, nie powinien
 * uczyć się drugiego sposobu czytania tych samych danych.
 *
 * Różnice wobec zwykłego kafelka są celowe i wynikają z kontekstu kreatora:
 * plakietka werdyktu, moc realna „dla Twojego auta" i czas ładowania.
 */
const Kandydat = ({ pozycja }) => {
  const { model, verdict, effective_power_kw: efektywna, message, czas, sklep, dostepnosc } = pozycja;
  const styl = WERDYKT[verdict] || WERDYKT.optymalny;
  // 'wallbox' to segment kategorii z routingu /model/:category/:slugId, nie slug listy.
  const url = buildModelUrl('wallbox', model);
  const cena = formatPrice(model.price_min, 0);
  const ofert = model.offer_count ?? 0;

  const parametry = [
    { icon: faIndustry, label: 'Producent', value: model.manufacturer },
    { icon: faBolt, label: 'Moc', value: kw(model.power_display_kw) },
    { icon: faPlug, label: 'Fazy', value: model.phases ? `${model.phases}-fazowy` : null },
    { icon: faStore, label: 'Sprzedawca', value: sklep },
    { icon: faBoxOpen, label: 'Dostępność', value: dostepnosc ? <AvailabilityBadge value={dostepnosc} /> : null },
    { icon: faCarSide, label: 'Dla Twojego auta', value: <span className="text-blue-700">{kw(efektywna)}</span> },
  ];

  return (
    <li className="rounded-2xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 md:flex-row">
        <div className="flex min-w-0 flex-1 gap-4">
          <div className="h-24 w-24 flex-shrink-0 rounded-lg border border-gray-100 bg-white p-1.5">
            <ProductImage
              src={model.img_url}
              alt={model.name}
              productType="wallbox"
              className="h-full w-full object-contain"
            />
          </div>

          <div className="min-w-0 flex-1">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs
                          font-semibold ring-1 ring-inset ${styl.klasa}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${styl.kropka}`} />
              {styl.label}
            </span>

            <h3 className="mt-1.5 font-bold text-gray-900">
              {url ? <Link to={url} className="hover:text-blue-600">{model.name}</Link> : model.name}
            </h3>

            <div className="mt-3">
              <SpecGrid items={parametry} className="grid-cols-2 sm:grid-cols-3" />
            </div>
          </div>
        </div>

        <div className="flex flex-row items-center justify-between gap-3 md:min-w-[180px] md:flex-col
                        md:justify-center md:border-l md:border-gray-100 md:pl-5">
          <div className="text-left md:text-center">
            <p className="text-xs uppercase tracking-wide text-gray-400">cena od</p>
            <p className="whitespace-nowrap text-2xl font-extrabold text-blue-600 md:text-3xl">
              {cena ? `${cena} zł` : 'Brak ceny'}
            </p>
          </div>
          {url && (
            <Link
              to={url}
              className="whitespace-nowrap rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold
                         text-white transition-colors hover:bg-blue-500"
            >
              {ofert === 1 ? 'Zobacz ofertę' : `Zobacz ${ofert} ${ofertySlowo(ofert)}`}
            </Link>
          )}
        </div>
      </div>

      {/* Uzasadnienie pod spodem, na całej szerokości: to zdanie o TYM aucie,
          a nie kolejny parametr produktu. */}
      <div className="mt-3 border-t border-gray-100 pt-3">
        <p className="text-sm leading-relaxed text-gray-600">{message}</p>
        {/* Czas pojawia się tylko dla aut z potwierdzoną pojemnością baterii —
            dla reszty nie ma z czego go policzyć i nie udajemy, że jest. */}
        {czas && (
          <p className="mt-1.5 flex items-center gap-1.5 text-sm text-gray-600">
            <BoltIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
            <span>
              Ładowanie {czas.okno_od_proc}–{czas.okno_do_proc}%:{' '}
              <span className="font-semibold text-gray-900">{czas.opis_okno}</span>
              {' '}(pełne: {czas.opis_0_100})
            </span>
          </p>
        )}
      </div>
    </li>
  );
};

const DoborLadowarki = () => {
  useDocumentTitle('Jaki wallbox do mojego auta');

  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const wybrane = params.get('auto') || '';

  const [auta, setAuta] = useState([]);
  const [marka, setMarka] = useState('');
  const [wynik, setWynik] = useState(null);
  const [ladowanie, setLadowanie] = useState(false);
  const [blad, setBlad] = useState(null);

  useEffect(() => {
    fetchProducts('wallbox_cars/')
      .then(setAuta)
      .catch(() => setBlad('Nie udało się pobrać listy aut.'));
  }, []);

  // Marka wynika z auta w adresie — dzięki temu wejście z gotowym linkiem
  // (albo powrót z karty produktu) ustawia oba pola, nie tylko drugie.
  useEffect(() => {
    if (!wybrane || !auta.length) return;
    const auto = auta.find((a) => a.slug === wybrane);
    if (auto) setMarka(auto.brand);
  }, [wybrane, auta]);

  useEffect(() => {
    if (!wybrane) { setWynik(null); return undefined; }
    let anulowane = false;
    setLadowanie(true);
    setBlad(null);
    fetchProducts(`wallbox_dobor/?car=${encodeURIComponent(wybrane)}&limit=12`)
      .then((d) => { if (!anulowane) setWynik(d); })
      .catch(() => { if (!anulowane) setBlad('Nie udało się policzyć dopasowania.'); })
      .finally(() => { if (!anulowane) setLadowanie(false); });
    return () => { anulowane = true; };
  }, [wybrane]);

  const marki = useMemo(
    () => [...new Set(auta.map((a) => a.brand))].sort((a, b) => a.localeCompare(b, 'pl')),
    [auta],
  );
  const modele = useMemo(() => auta.filter((a) => a.brand === marka), [auta, marka]);

  // `replace`, nie `push`: przeklikiwanie modeli nie ma zasypywać historii
  // przeglądarki — „wstecz" ma wracać tam, skąd użytkownik przyszedł.
  const ustawAuto = (slug) => {
    if (slug) setParams({ auto: slug }, { replace: true });
    else navigate('/dobor-ladowarki', { replace: true });
  };

  const wybierzMarke = (nowa) => {
    setMarka(nowa);
    const dostepne = auta.filter((a) => a.brand === nowa);
    // Marka z jednym modelem nie daje wyboru — wybieramy go od razu.
    ustawAuto(dostepne.length === 1 ? dostepne[0].slug : '');
  };

  const rek = wynik?.rekomendacja;
  const auto = wynik?.car;

  return (
    <div className="mx-auto mb-16 mt-8 max-w-4xl px-4 md:px-8">
      <Breadcrumbs />

      <h1 className="mt-6 text-3xl font-bold text-gray-900">Jaki wallbox do mojego auta</h1>
      <p className="mt-2 text-gray-600">
        Moc ładowania z gniazdka ogranicza słabsze ogniwo: ładowarka pokładowa auta albo wallbox.
        Wybierz samochód, a policzymy, ile realnie przyjmie i czy dopłata za mocniejsze
        urządzenie cokolwiek zmieni.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-semibold text-gray-700">1. Marka</span>
          <select
            value={marka}
            onChange={(e) => wybierzMarke(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-800"
          >
            <option value="">Wybierz markę</option>
            {marki.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>

        <label className="block">
          <span className={`text-sm font-semibold ${marka ? 'text-gray-700' : 'text-gray-400'}`}>
            2. Model
          </span>
          <select
            value={wybrane}
            onChange={(e) => ustawAuto(e.target.value)}
            disabled={!marka}
            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-800
                       disabled:bg-gray-100 disabled:text-gray-400"
          >
            <option value="">{marka ? 'Wybierz model' : 'Najpierw wybierz markę'}</option>
            {modele.map((a) => (
              // Wariant i rocznik MUSZĄ być w etykiecie: jeden model ma kilka
              // pakietów baterii o różnych ładowarkach pokładowych.
              <option key={a.slug} value={a.slug}>
                {[a.model, a.variant, a.rocznik && `(${a.rocznik})`].filter(Boolean).join(' ')}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="mt-2 text-xs text-gray-500">
        Baza obejmuje {wersjeAut(auta.length)}. Jeśli nie wiesz, który pakiet baterii masz,
        sprawdź w dowodzie rejestracyjnym albo w instrukcji pojazdu. Wersje tego samego
        modelu różnią się mocą ładowania AC.
      </p>

      {blad && <p className="mt-6 text-red-600">{blad}</p>}
      {ladowanie && <p className="mt-6 text-gray-500">Liczę dopasowanie…</p>}

      {auto && (
        <>
          <section className="mt-8 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex items-start gap-4 border-b border-gray-100
                            bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-5">
              {/* Grafiki są opcjonalne — komponent chowa je, gdy pliku brak,
                  więc katalog obrazków może rosnąć bez zmian w kodzie. */}
              <div className="hidden flex-shrink-0 sm:block">
                <ObrazekAuta
                  src={sciezkiZdjeciaAuta(auto)}
                  alt={auto.full_name}
                  className="h-20 w-32 object-contain"
                />
                {/* Podpis jest wymagany licencjami CC BY / CC BY-SA — patrz
                    komentarz w PodpisGrafiki. */}
                <PodpisGrafiki sciezki={sciezkiZdjeciaAuta(auto)} className="mt-1 w-32 text-center" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <ObrazekAuta
                    src={sciezkaLogaMarki(auto.brand)}
                    alt={auto.brand}
                    className="h-6 w-6 flex-shrink-0 object-contain"
                  />
                  <h2 className="truncate text-lg font-bold text-gray-900">{auto.full_name}</h2>
                </div>

                {/* Parametry jako oddzielone kafelki, nie zdanie ciągiem —
                    to są liczby do porównania, a nie tekst do czytania. */}
                <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-gray-500">Ładowarka pokładowa</dt>
                    <dd className="font-semibold text-gray-900">
                      {kw(auto.obc_ac_kw)} · {auto.phases === 1 ? '1-fazowa' : `${auto.phases}-fazowa`}
                    </dd>
                  </div>
                  {auto.battery_usable_kwh != null && (
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">Bateria</dt>
                      <dd className="font-semibold text-gray-900">
                        {String(auto.battery_usable_kwh).replace('.', ',')} kWh
                      </dd>
                    </div>
                  )}
                  {auto.rocznik && (
                    <div>
                      <dt className="text-xs uppercase tracking-wide text-gray-500">Rocznik</dt>
                      <dd className="font-semibold text-gray-900">{auto.rocznik}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>

            <div className="p-5">
              <p className="text-gray-700">{rek?.uzasadnienie}</p>
              <p className="mt-3 rounded-lg bg-blue-600 px-4 py-2.5 font-semibold text-white">
                Szukaj wallboxa: {kw(rek?.power_kw)},{' '}
                {rek?.phases === 1 ? 'jednofazowego' : `${rek?.phases}-fazowego`}
              </p>

              {wynik.warnings?.length > 0 && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-amber-900">
                    <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
                    Zanim kupisz: zastrzeżenia do tego modelu
                  </p>
                  <ul className="mt-1.5 space-y-1 pl-6 text-sm text-amber-900">
                    {wynik.warnings.map((w) => (
                      <li key={w} className="list-disc marker:text-amber-500">{w}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </section>

          <h2 className="mb-3 mt-8 text-xl font-bold text-gray-900">
            Ładowarki z katalogu ocenione pod to auto
          </h2>
          {wynik.kandydaci?.length > 0 ? (
            <ul className="space-y-3">
              {wynik.kandydaci.map((k) => <Kandydat key={k.model.id} pozycja={k} />)}
            </ul>
          ) : (
            <p className="text-gray-500">
              W katalogu nie ma dziś modelu z potwierdzoną mocą i liczbą faz.
            </p>
          )}
          {wynik.odrzucone_count > 0 && (
            <p className="mt-3 text-sm text-gray-500">
              Pokazujemy {wynik.kandydaci.length} najlepiej dopasowanych; pozostałe{' '}
              {wynik.odrzucone_count} modeli znajdziesz w{' '}
              <Link to="/ladowarki" className="text-blue-600 hover:underline">katalogu ładowarek</Link>.
            </p>
          )}
        </>
      )}

      {/* Zapytanie trafiło w kilka wersji tego samego modelu — pokazujemy je
          do wyboru zamiast twierdzić, że auta nie znamy. */}
      {!auto && wynik?.warianty?.length > 0 && (
        <section className="mt-8 rounded-xl border border-gray-200 bg-white p-5">
          <p className="font-semibold text-gray-900">Wskaż wersję</p>
          <p className="mt-1 text-sm text-gray-600">{wynik.warnings?.[0]}</p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {wynik.warianty.map((w) => (
              <li key={w.slug}>
                <button
                  type="button"
                  onClick={() => ustawAuto(w.slug)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700
                             hover:border-blue-400 hover:text-blue-700"
                >
                  {w.full_name}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Poradniki: do 2026-10 narzędzie nie linkowało do żadnego artykułu (raport ładowarek EV). */}
      <section className="mt-10 border-t border-gray-100 pt-6">
        <h2 className="text-lg font-bold text-gray-900">Poradniki</h2>
        <ul className="mt-2 space-y-1.5 text-sm">
          {/* Strona o aucie (seo_auta albo artykuł Bazy wiedzy): adres podaje API w `strona`. */}
          {auto?.strona && (
            <li>
              <Link to={auto.strona} className="font-semibold text-blue-600 hover:underline">
                Jaki wallbox do {auto.brand} {auto.model}? Wersje, czasy ładowania i pasujące wallboxy
              </Link>
            </li>
          )}
          {PORADNIKI_OGOLNE.map(([href, tytul]) => (
            <li key={href}>
              <Link to={href} className="text-blue-600 hover:underline">{tytul}</Link>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default DoborLadowarki;
